import { CompanyFilterBaseDataProvider } from './CompanyFilterBaseDataProvider';
import { GetTargetsRequest, GetTargetsResponse } from '@nirvana/shared/dtos/types/alerts/gz-console';
import { GetCompaniesRequest, GetCompaniesResponse } from '@nirvana/shared/dtos/types/protected-entities/gz-console';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { CompanyEndpointMethod } from '../../../quarantine/exchangeProtection/types';
import { Target } from '@nirvana/shared/company-helper/nestjs';

export class CompanyFilterIntegrityMonitorEventsDataProvider extends CompanyFilterBaseDataProvider {
  private static async getRequiredTargets(request: GetTargetsRequest): Promise<Array<Target>> {
    const selectedCompanyIds = request.params?.companyIds ?? [];

    if (!selectedCompanyIds.length) {
      return [];
    }

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'Company',
      method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
      payload: {
        page: 1,
        // If the user had selected more than 100 companies in the selector, keep only the first 100 selected
        limit: 100,
        options: {
          source: 'fromIntegrityMonitorEvents',
          companyIds: selectedCompanyIds,
          // Bypass the 25 companies max limit
          ignoreLimit: true,
        },
      },
    });

    return response.data.map(({ _id, name }) => ({ companyId: _id, companyName: name }));
  }

  private static async getInitialTargetsResponse(): Promise<GetCompaniesResponse> {
    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'Company',
      method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
      payload: {
        page: 1,
        limit: 10,
        options: {
          source: 'fromIntegrityMonitorEvents',
        },
      },
    });

    return {
      targets: response.data.map(({ _id, name }) => ({ companyId: _id, companyName: name })),
      totalCount: response.count,
    };
  }

  async getInitialTargets(request: GetTargetsRequest): Promise<GetTargetsResponse | undefined> {
    try {
      const [requiredTargets, responseInitialTargets] = await Promise.all([
        CompanyFilterIntegrityMonitorEventsDataProvider.getRequiredTargets(request),
        CompanyFilterIntegrityMonitorEventsDataProvider.getInitialTargetsResponse(),
      ]);

      const targets = [...requiredTargets, ...responseInitialTargets.targets];

      // It is possible that some of the selected companies have been fetched twice, as being in the first page
      const uniqueTargets = [...new Map(targets.map(target => [target.companyId, target])).values()];

      return {
        defaultTarget: [this.dependencies.getCurrentUserCompany().id],
        targets: uniqueTargets,
        totalCount: responseInitialTargets.totalCount,
      };
    } catch (e) {
      throw new Error(e);
    }
  }

  async getCompanies(request: GetCompaniesRequest): Promise<GetCompaniesResponse | undefined> {
    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.ENDPOINTS,
        endpoint: 'Company',
        method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
        payload: {
          companyName: request.companyName,
          limit: request.limit,
          page: request.page,
          options: {
            source: 'fromIntegrityMonitorEvents',
          },
        },
      });

      return {
        targets: response.data.map(({ _id, name }) => ({ companyId: _id, companyName: name })),
        totalCount: response.count,
      };
    } catch (e) {
      throw new Error(e);
    }
  }
}
